<template>
  <div class="icon-slider">
    <div
      class="icon-slider__slider"
      :class="{ 'icon-slider__slider--ready': slider }">
      <!-- slider thumbs with icons -->
      <div class="icon-slider__thumbs">
        <div class="icon-slider__thumbs-viewport" ref="sliderThumbs">
          <div class="icon-slider__thumbs-container">
            <template v-for="slide in slides" :key="slide.id">
              <div
                class="icon-slider__slide"
                :class="{
                  'icon-slider__slide--active': slide.id === activeSlideId,
                }"
                @click="moveToSlideIndex(slide.id)">
                <ResponsiveImage :images="slide.icon" />
              </div>
            </template>
          </div>
        </div>
      </div>

      <!-- main slider content -->
      <div
        class="icon-slider__main"
        :style="{ '--slider-height': `${sliderHeight}px` }">
        <div class="icon-slider__main-slider">
          <div class="icon-slider__main-viewport" ref="sliderNode">
            <div class="icon-slider__main-container">
              <template v-for="slide in slides" :key="slide.id">
                <div
                  class="icon-slider__slide"
                  :class="{
                    'icon-slider__slide--active': slide.id === activeSlideId,
                  }">
                  <!-- slide texts -->
                  <div class="icon-slider__content-wrapper">
                    <Transition name="appear" mode="out-in" appear>
                      <div :key="activeSlideId" class="icon-slider__content">
                        <span v-if="slide.label">{{ slide.label }}</span>
                        <h3>{{ slide.headline }}</h3>
                        <div
                          v-html="slide.description"
                          class="text-formatted"></div>
                      </div>
                    </Transition>
                  </div>
                  <!-- slide image -->
                  <div class="icon-slider__image">
                    <ResponsiveImage :images="slide.image" />
                  </div>
                </div>
              </template>
            </div>
          </div>
        </div>

        <div v-if="enableSlider" class="icon-slider__navigation">
          <button
            :class="{
              'navigation-button--disabled': checkArrowsDisabled(true),
            }"
            class="navigation-button navigation-button--prev"
            :title="t.previous"
            @click="navigationClick(true)"></button>
          <button
            :class="{
              'navigation-button--disabled': checkArrowsDisabled(),
            }"
            class="navigation-button navigation-button--next"
            :title="t.next"
            @click="navigationClick()"></button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// embla slider
import EmblaCarousel from 'embla-carousel';
// components
import ResponsiveImage from './elements/ResponsiveImage';
// utils
import { isMobile } from '../utils/isMobile';
import translationStrings from '../translations/translationStrings';

export default {
  name: 'icon-slider',
  components: { ResponsiveImage },
  props: {
    slides: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      slider: null,
      sliderThumbs: null,
      activeSlideId: 0,
      sliderHeight: '',
      onMobile: isMobile(),
      t: translationStrings,
    };
  },
  computed: {
    enableSlider() {
      return this.slides.length > 1;
    },
  },
  mounted() {
    this.initSlider();
    this.initThumbs();

    this.slider.on('select', this.updateCurrentSlide);
    // you can use this for listening to resize event
    // instead of window.addEventListener()
    this.slider.on('resize', this.handleResize);

    // get height of the slides
    this.getSliderHeight();
  },
  methods: {
    initSlider() {
      this.slider = EmblaCarousel(this.$refs.sliderNode, {
        active: this.enableSlider,
        // the 'draggable' option is not in docs,
        // but it seems to do the job better as watchDrag ...
        // (leaving both in, in case something changes)
        draggable: false,
        watchDrag: false,
      });
    },
    initThumbs() {
      this.sliderThumbs = EmblaCarousel(this.$refs.sliderThumbs, {
        active: this.onMobile,
        axis: this.onMobile ? 'x' : 'y',
        containScroll: 'keepSnaps',
        // ... tho, it does work for thumbs 🤷
        watchDrag: this.onMobile,
      });
    },
    moveToSlideIndex(i) {
      if (this.slider) {
        this.slider.scrollTo(i);
      }
      if (this.sliderThumbs) {
        // this scrolls thumb into view (if outside container)
        this.sliderThumbs.scrollTo(i);
      }
      this.activeSlideId = i;
    },
    updateCurrentSlide() {
      this.activeSlideId = this.slider.selectedScrollSnap();
    },
    getSliderHeight() {
      // need this bc position absolute for fade effect
      let height = 0;

      if (this.slider) {
        this.slider.slideNodes().forEach((s) => {
          // need to check for different height on mobile
          const contentHeight = s.querySelector(
            '.icon-slider__content',
          ).scrollHeight;

          height = contentHeight > height ? contentHeight : height;
        });
      }

      this.sliderHeight = height;
    },
    navigationClick(isBack = false) {
      this.moveToSlideIndex(
        isBack ? this.activeSlideId - 1 : this.activeSlideId + 1,
      );
    },
    checkArrowsDisabled(isBack = false) {
      return isBack
        ? this.activeSlideId === 0
        : this.slides[this.slides.length - 1].id === this.activeSlideId;
    },
    handleResize() {
      // update the mobile variable
      this.onMobile = isMobile();

      // recalculate the slider height
      this.sliderHeight = '';
      this.getSliderHeight();
    },
  },
};
</script>
